import { PlayerAvatar, GoldNugget } from "../../components/player-avatar";
import { GoldCard } from "../cards/card-face";
import type { DisplayProjection, PlayerProjection } from "@saboteur/contracts";

import "../board/game-board.css";

type RoundOutcome = NonNullable<DisplayProjection["roundOutcome"]>;
type PublicParticipant = DisplayProjection["participants"][number];
type GoldChoice = PlayerProjection["self"]["pendingGoldChoices"][number];
type InspectedGoal = PlayerProjection["self"]["inspectedGoals"][number];

export interface RoundResultsProps {
  readonly outcome: RoundOutcome;
  readonly participants: readonly PublicParticipant[];
  readonly privateGold?: number;
  readonly selfRole: PlayerProjection["self"]["role"];
  readonly inspectedGoals: readonly InspectedGoal[];
  readonly pendingGoldChoices: readonly GoldChoice[];
  readonly onChooseGold?: (goldCardId: string) => void;
  readonly busy?: boolean;
  readonly error?: string | undefined;
  readonly phase?: "GOLD_SELECTION" | "BETWEEN_ROUNDS";
}

function outcomeHeading(outcome: RoundOutcome) {
  if (outcome.winningTeam === "MINERS") return "Gold found!";
  if (outcome.winningTeam === "SABOTEURS") return "Saboteurs win!";
  return "No gold this time";
}

export function RoundResults({
  outcome,
  participants,
  privateGold = 0,
  pendingGoldChoices,
  onChooseGold,
  busy = false,
  error,
  phase = "BETWEEN_ROUNDS",
}: RoundResultsProps) {
  return (
    <section className="surface stack round-results" aria-labelledby="round-results-title">
      <div>
        <p className="eyebrow">Round {outcome.roundNumber} complete</p>
        <h2 id="round-results-title">{outcomeHeading(outcome)}</h2>
      </div>

      <p className="results-caption">Roles revealed</p>
      <div className="round-crew" aria-label="Roles revealed">
        {participants.map((p) => (
          <div
            key={p.participantId}
            className={
              p.revealedRole ===
              (outcome.winningTeam === "MINERS"
                ? "MINER"
                : outcome.winningTeam === "SABOTEURS"
                  ? "SABOTEUR"
                  : null)
                ? "round-crew__winner"
                : ""
            }
          >
            <PlayerAvatar seat={p.avatarIndex ?? p.seatIndex} />
            <strong>{p.displayName}</strong>
            <small>{p.revealedRole?.toLowerCase()}</small>
          </div>
        ))}
      </div>
      <div className="private-score" aria-label="Your private gold total">
        <GoldNugget />
        <strong>{privateGold}</strong>
        <span>
          Your gold<small>Your total across rounds</small>
        </span>
      </div>
      {pendingGoldChoices.length ? (
        <fieldset disabled={busy}>
          <legend>Choose your gold reward</legend>
          <p className="results-caption">Pick one · yours to keep</p>
          <div className="button-row">
            {pendingGoldChoices.map((choice) => (
              <button
                type="button"
                key={choice.goldCardId}
                onClick={() => onChooseGold?.(choice.goldCardId)}
              >
                <GoldCard value={choice.value} />
                {choice.value} gold
              </button>
            ))}
          </div>
        </fieldset>
      ) : null}

      {pendingGoldChoices.length ? null : (
        <p role="status">
          {phase === "GOLD_SELECTION" ? "Sharing the gold…" : "Waiting for the host…"}
        </p>
      )}
      {error ? (
        <p role="alert" className="action-error">
          {error}
        </p>
      ) : null}
    </section>
  );
}
